import type { ModelEntry, ProviderDefinition, ProviderId } from './types.js';

export const PROVIDER_DEFINITIONS: Record<ProviderId, ProviderDefinition> = {
  gemini: {
    id: 'gemini',
    displayName: 'Gemini',
    idPattern: /^gemini-/i,
    supportsAgents: false,
    supportsEmbeddings: true,
    supportsContainerExecution: false,
  },
  openrouter: {
    id: 'openrouter',
    displayName: 'OpenRouter',
    idPattern: /^[^\s/]+\/[^\s/].*(?::free)?$/i,
    supportsAgents: true,
    supportsEmbeddings: false,
    supportsContainerExecution: false,
  },
  anthropic: {
    id: 'anthropic',
    displayName: 'Anthropic',
    idPattern: /^claude-/i,
    supportsAgents: true,
    supportsEmbeddings: false,
    supportsContainerExecution: true,
  },
};

export const MODEL_CATALOG: ModelEntry[] = [
  {
    id: 'gemini-2.5-flash',
    provider: 'gemini',
    displayName: 'Gemini 2.5 Flash',
    aliases: [],
    pricing: { inputPerMillion: 0.3, outputPerMillion: 2.5 },
    capabilities: ['chat', 'vision'],
    status: 'ga',
    contextLength: 1_048_576,
    costTier: 'low',
    defaultMaxOutputTokens: 8_192,
  },
  {
    id: 'gemini-2.5-flash-lite',
    provider: 'gemini',
    displayName: 'Gemini 2.5 Flash Lite',
    aliases: [],
    pricing: { inputPerMillion: 0.1, outputPerMillion: 0.4 },
    capabilities: ['chat', 'vision'],
    status: 'ga',
    contextLength: 1_048_576,
    costTier: 'low',
    defaultMaxOutputTokens: 8_192,
  },
  {
    id: 'gemini-3-flash-preview',
    provider: 'gemini',
    displayName: 'Gemini 3 Flash Preview',
    aliases: [],
    pricing: { inputPerMillion: 0.25, outputPerMillion: 1.5 },
    capabilities: ['chat', 'vision'],
    status: 'preview',
    contextLength: 1_048_576,
    costTier: 'low',
    defaultMaxOutputTokens: 8_192,
  },
  {
    id: 'gemini-3.1-flash-lite-preview',
    provider: 'gemini',
    displayName: 'Gemini 3.1 Flash Lite Preview',
    aliases: [],
    pricing: { inputPerMillion: 0.25, outputPerMillion: 1.5 },
    capabilities: ['chat', 'vision'],
    status: 'preview',
    contextLength: 1_048_576,
    costTier: 'low',
    defaultMaxOutputTokens: 8_192,
  },
  {
    id: 'gemini-3.1-pro-preview',
    provider: 'gemini',
    displayName: 'Gemini 3.1 Pro Preview',
    aliases: [],
    pricing: { inputPerMillion: 2, outputPerMillion: 12 },
    capabilities: ['chat', 'vision'],
    status: 'preview',
    contextLength: 1_048_576,
    costTier: 'medium',
    defaultMaxOutputTokens: 8_192,
  },
  {
    id: 'gemini-embedding-001',
    provider: 'gemini',
    displayName: 'Gemini Embedding 001',
    aliases: [],
    pricing: { inputPerMillion: 0, outputPerMillion: 0 },
    capabilities: ['embedding'],
    status: 'ga',
    contextLength: null,
    costTier: 'free',
    defaultMaxOutputTokens: null,
  },
  {
    id: 'gemini-2.0-flash',
    provider: 'gemini',
    displayName: 'Gemini 2.0 Flash',
    aliases: [],
    pricing: { inputPerMillion: 0.1, outputPerMillion: 0.4 },
    capabilities: ['chat', 'vision'],
    status: 'deprecated',
    contextLength: 1_048_576,
    costTier: 'low',
    defaultMaxOutputTokens: 8_192,
  },
  {
    id: 'gemini-2.0-flash-lite',
    provider: 'gemini',
    displayName: 'Gemini 2.0 Flash Lite',
    aliases: [],
    pricing: { inputPerMillion: 0.1, outputPerMillion: 0.4 },
    capabilities: ['chat', 'vision'],
    status: 'deprecated',
    contextLength: 1_048_576,
    costTier: 'low',
    defaultMaxOutputTokens: 8_192,
  },
  {
    id: 'anthropic/claude-sonnet-4-6',
    provider: 'openrouter',
    displayName: 'Claude Sonnet 4.6',
    aliases: [],
    pricing: { inputPerMillion: 3, outputPerMillion: 15 },
    capabilities: ['chat', 'agent_tool_use', 'vision'],
    status: 'ga',
    contextLength: 200_000,
    costTier: 'medium',
    defaultMaxOutputTokens: 16_384,
  },
  {
    id: 'anthropic/claude-sonnet-4',
    provider: 'openrouter',
    displayName: 'Claude Sonnet 4',
    aliases: [],
    pricing: { inputPerMillion: 3, outputPerMillion: 15 },
    capabilities: ['chat', 'agent_tool_use', 'vision'],
    status: 'ga',
    contextLength: 200_000,
    costTier: 'medium',
    defaultMaxOutputTokens: 16_384,
  },
  {
    id: 'openai/gpt-4o-mini',
    provider: 'openrouter',
    displayName: 'GPT-4o Mini',
    aliases: [],
    pricing: { inputPerMillion: 0.15, outputPerMillion: 0.6 },
    capabilities: ['chat', 'agent_tool_use', 'vision'],
    status: 'ga',
    contextLength: 128_000,
    costTier: 'low',
    defaultMaxOutputTokens: 4_096,
  },
  {
    id: 'meta-llama/llama-3.3-70b-instruct',
    provider: 'openrouter',
    displayName: 'Llama 3.3 70B Instruct',
    aliases: [],
    pricing: { inputPerMillion: 0.1, outputPerMillion: 0.32 },
    capabilities: ['chat', 'agent_tool_use'],
    status: 'ga',
    contextLength: 131_072,
    costTier: 'low',
    defaultMaxOutputTokens: 4_096,
  },
  {
    id: 'claude-sonnet-4-6',
    provider: 'anthropic',
    displayName: 'Claude Sonnet 4.6',
    aliases: [
      'anthropic/claude-sonnet-4-6',
      'anthropic/claude-sonnet-4',
      'claude-sonnet-4',
      'claude-sonnet-4-20250514',
    ],
    pricing: { inputPerMillion: 3, outputPerMillion: 15 },
    capabilities: ['chat', 'agent_tool_use', 'container_execution', 'vision'],
    status: 'ga',
    contextLength: 200_000,
    costTier: 'medium',
    defaultMaxOutputTokens: 16_384,
  },
  {
    id: 'claude-sonnet-4-0',
    provider: 'anthropic',
    displayName: 'Claude Sonnet 4.0',
    aliases: [],
    pricing: { inputPerMillion: 3, outputPerMillion: 15 },
    capabilities: ['chat', 'agent_tool_use', 'container_execution', 'vision'],
    status: 'ga',
    contextLength: 200_000,
    costTier: 'medium',
    defaultMaxOutputTokens: 16_384,
  },
  {
    id: 'claude-3-5-haiku-latest',
    provider: 'anthropic',
    displayName: 'Claude 3.5 Haiku',
    aliases: [],
    pricing: { inputPerMillion: 0.8, outputPerMillion: 4 },
    capabilities: ['chat', 'agent_tool_use', 'container_execution'],
    status: 'ga',
    contextLength: 200_000,
    costTier: 'low',
    defaultMaxOutputTokens: 8_192,
  },
  {
    id: 'claude-opus-4-6',
    provider: 'anthropic',
    displayName: 'Claude Opus 4.6',
    aliases: ['anthropic/claude-opus-4-6', 'claude-opus-4', 'claude-opus-4-1-20250805'],
    pricing: { inputPerMillion: 15, outputPerMillion: 75 },
    capabilities: ['chat', 'agent_tool_use', 'container_execution', 'vision'],
    status: 'deprecated',
    contextLength: 200_000,
    costTier: 'high',
    defaultMaxOutputTokens: 16_384,
  },
  {
    id: 'claude-haiku-4-5-20251001',
    provider: 'anthropic',
    displayName: 'Claude Haiku 4.5',
    aliases: ['anthropic/claude-haiku-4-5', 'claude-haiku-4-5'],
    pricing: { inputPerMillion: 0.8, outputPerMillion: 4 },
    capabilities: ['chat', 'agent_tool_use', 'container_execution'],
    status: 'deprecated',
    contextLength: 200_000,
    costTier: 'low',
    defaultMaxOutputTokens: 8_192,
  },
];
